import * as React from 'react';
import {
  Bullseye,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter
} from '@patternfly/react-core';
import { Td, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import { ApiError } from '@app/utils/ApiError';

interface DataLoadErrorProps {
  /** The failure reported by the store, from useEntity(). */
  error: ApiError | Error;
  /** What the page was trying to load, e.g. "instructors". Used in the heading. */
  entityLabel?: string;
  onRetry?: () => void;
}

/**
 * Tier 2 of API error handling: shown in place of a list or a form when its own
 * load failed, so the page states the reason and offers a way out instead of
 * rendering an empty table that looks like "no records exist".
 */
const DataLoadError: React.FunctionComponent<DataLoadErrorProps> = ({ error, entityLabel, onRetry }) => {
  const kind = error instanceof ApiError ? error.kind : 'unknown';

  const body = kind === 'unreachable'
    ? 'The allocation service could not be reached. It may be restarting, or your connection may be down.'
    : error.message;

  return (
    <Bullseye>
      <EmptyState
        status="danger"
        headingLevel="h4"
        icon={ExclamationCircleIcon}
        titleText={entityLabel ? `Unable to load ${entityLabel}` : 'Unable to load this data'}
      >
        <EmptyStateBody>{body}</EmptyStateBody>
        {onRetry && (
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={onRetry}>Try again</Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        )}
      </EmptyState>
    </Bullseye>
  );
};

interface DataLoadErrorRowProps extends DataLoadErrorProps {
  /** Must span every column of the table so the message is centred under the header. */
  colSpan: number;
}

/**
 * DataLoadError placed inside a table row, for list views that render their
 * empty/error state inside <Tbody>.
 */
const DataLoadErrorRow: React.FunctionComponent<DataLoadErrorRowProps> = ({ colSpan, ...props }) => (
  <Tr>
    <Td colSpan={colSpan}>
      <DataLoadError {...props} />
    </Td>
  </Tr>
);

export { DataLoadError, DataLoadErrorRow };
